/* Borderline winter 26/27 · rail pager (P0: snippets/bl-rail-nav.liquid, assets/bl-rail.css).
   <bl-rail-nav data-track="id"> pages the rail with that id by whole cards: "01 / 03", ‹ › disabled at the ends,
   hidden while one page holds everything. It pads the rail's end (.rail__end) so the scroll maximum is a whole
   number of card pitches, and follows the rail when its cards change (shelf tabs) or it resizes.
   window.BL.railNav(track, host, { prevLabel, nextLabel }) builds the same pager inside host for script-built rails.
   Also writes --bl-sbw (the page scroll container's scrollbar width) so --bl-vw is the visible viewport width:
   at >= 990px Horizon scrolls .page-wrapper, where 100vw would include its scrollbar. */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const squeeze = matchMedia('(min-width: 990px)');
const ARROWS = {
  prev: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M12.5 4.5 7 10l5.5 5.5"/></svg>',
  next: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M7.5 4.5 13 10l-5.5 5.5"/></svg>',
};
let railCount = 0;

/** @param {number} value */
const pad2 = (value) => String(value).padStart(2, '0');

/* ---------- the visible viewport width ---------- */
function measureScrollbar() {
  const root = document.documentElement;
  const wrapper = squeeze.matches ? document.querySelector('.page-wrapper') : null;
  const width = wrapper instanceof HTMLElement ? wrapper.offsetWidth - wrapper.clientWidth : window.innerWidth - root.clientWidth;
  const value = `${Math.max(0, Math.round(width))}px`;
  if (root.style.getPropertyValue('--bl-sbw') !== value) root.style.setProperty('--bl-sbw', value);
}

let scrollbarObserver = null;
function watchScrollbar() {
  measureScrollbar();
  scrollbarObserver?.disconnect();
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper && 'ResizeObserver' in window) {
    scrollbarObserver = new ResizeObserver(() => measureScrollbar());
    scrollbarObserver.observe(wrapper);
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchScrollbar, { once: true });
else watchScrollbar();
window.addEventListener('resize', measureScrollbar, { passive: true });
squeeze.addEventListener('change', measureScrollbar);

/* ---------- the pager ---------- */
class BlRailNav extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  #frame = 0;

  connectedCallback() {
    this.#ensureMarkup();
    const track = this.dataset.track ? document.getElementById(this.dataset.track) : null;
    this.bind(track);
  }

  disconnectedCallback() {
    this.#unbind();
  }

  /** @returns {HTMLElement | null} */
  get track() {
    return this.#track;
  }

  /**
   * Attach the pager to a rail (re-binding is idempotent).
   * @param {HTMLElement | null} track
   */
  bind(track) {
    this.#unbind();
    if (!(track instanceof HTMLElement)) return;
    this.#track = track;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    track.addEventListener('scroll', this.#schedule, { passive: true, signal });
    this.querySelector('.rnav__b--prev')?.addEventListener('click', () => this.go(-1), { signal });
    this.querySelector('.rnav__b--next')?.addEventListener('click', () => this.go(1), { signal });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.refresh());
      this.#resizeObserver.observe(track);
    }
    this.#mutationObserver = new MutationObserver(() => this.refresh());
    this.#mutationObserver.observe(track, { childList: true });

    this.refresh();
    if (document.fonts?.ready) document.fonts.ready.then(() => this.refresh());
  }

  #unbind() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#track = null;
  }

  #ensureMarkup() {
    if (this.querySelector('.rnav__b--next')) return;
    this.classList.add('rnav');
    const controls = this.dataset.track || '';
    this.innerHTML = `<span class="rnav__n" aria-hidden="true"></span><button type="button" class="rnav__b rnav__b--prev" disabled>${ARROWS.prev}</button><button type="button" class="rnav__b rnav__b--next">${ARROWS.next}</button>`;
    const prev = this.querySelector('.rnav__b--prev');
    const next = this.querySelector('.rnav__b--next');
    prev?.setAttribute('aria-label', this.dataset.prevLabel || 'Previous');
    next?.setAttribute('aria-label', this.dataset.nextLabel || 'Next');
    if (controls) {
      prev?.setAttribute('aria-controls', controls);
      next?.setAttribute('aria-controls', controls);
    }
  }

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.sync();
    });
  };

  /** The rail's cards, without the end pad and anything not laid out. */
  get cells() {
    const track = this.#track;
    if (!track) return [];
    return /** @type {HTMLElement[]} */ ([...track.children]).filter(
      (cell) => !cell.classList.contains('rail__end') && cell.offsetWidth > 0
    );
  }

  /** The distance from one card's start to the next card's start. */
  get pitch() {
    const cells = this.cells;
    if (cells.length > 1) return cells[1].offsetLeft - cells[0].offsetLeft;
    return cells[0]?.offsetWidth || this.#track?.clientWidth || 1;
  }

  /** Whole cards per page: the visible width from the column line to the window's edge. */
  get perPage() {
    const track = this.#track;
    const cells = this.cells;
    if (!track || !cells.length) return 1;
    const pitch = this.pitch;
    const gap = Math.max(0, pitch - cells[0].offsetWidth);
    return Math.max(1, Math.floor((track.clientWidth + gap + 1) / pitch));
  }

  get pages() {
    return Math.max(1, Math.ceil(this.cells.length / this.perPage));
  }

  get page() {
    const track = this.#track;
    if (!track) return 1;
    const max = track.scrollWidth - track.clientWidth;
    const pages = this.pages;
    if (track.scrollLeft >= max - 2) return pages;
    return Math.max(1, Math.min(pages, Math.round(track.scrollLeft / (this.perPage * this.pitch)) + 1));
  }

  /** Re-measure: pad the end, then update the count and the buttons. */
  refresh() {
    this.fitEnd();
    this.sync();
  }

  /** Make the scroll maximum a whole number of pitches, so the last page shows whole cards. */
  fitEnd() {
    const track = this.#track;
    if (!track) return;
    let pad = /** @type {HTMLElement | null} */ (track.querySelector(':scope > .rail__end'));
    if (!pad) {
      pad = document.createElement(track.tagName === 'UL' || track.tagName === 'OL' ? 'li' : 'span');
      pad.className = 'rail__end';
      pad.setAttribute('aria-hidden', 'true');
      this.#mutationObserver?.disconnect();
      track.appendChild(pad);
      this.#mutationObserver?.observe(track, { childList: true });
    } else if (pad !== track.lastElementChild) {
      this.#mutationObserver?.disconnect();
      track.appendChild(pad);
      this.#mutationObserver?.observe(track, { childList: true });
    }
    pad.style.width = '0px';
    const pitch = this.pitch;
    const max = track.scrollWidth - track.clientWidth;
    if (pitch > 0 && max > 0 && this.cells.length > 1) {
      const whole = Math.ceil(max / pitch - 0.01) * pitch;
      pad.style.width = `${Math.max(0, Math.round(whole - max))}px`;
    }
  }

  sync() {
    const track = this.#track;
    if (!track) return;
    const pages = this.pages;
    this.hidden = pages <= 1;
    if (pages <= 1) return;
    const current = this.page;
    const count = this.querySelector('.rnav__n');
    if (count) count.textContent = `${pad2(current)} / ${pad2(pages)}`;
    const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('.rnav__b--prev'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('.rnav__b--next'));
    const max = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }

  /** @param {number} direction - 1 forward, -1 back */
  go(direction) {
    const track = this.#track;
    if (!track) return;
    track.scrollBy({ left: direction * this.perPage * this.pitch, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  }
}

if (!customElements.get('bl-rail-nav')) {
  customElements.define('bl-rail-nav', BlRailNav);
}

/**
 * Build (or reuse) a pager for a rail inside host.
 * @param {HTMLElement} track - The rail (class "rail").
 * @param {HTMLElement} host - Where the pager goes (a heading row, the shelf's end cell…).
 * @param {{ prevLabel?: string, nextLabel?: string }} [options]
 * @returns {BlRailNav | null}
 */
function railNav(track, host, options = {}) {
  if (!(track instanceof HTMLElement) || !(host instanceof HTMLElement)) return null;
  if (!track.id) track.id = `bl-rail-${++railCount}`;
  let nav = /** @type {BlRailNav | null} */ (host.querySelector('bl-rail-nav'));
  if (!nav) {
    nav = /** @type {BlRailNav} */ (document.createElement('bl-rail-nav'));
    nav.className = 'rnav';
    nav.hidden = true;
    nav.dataset.track = track.id;
    if (options.prevLabel) nav.dataset.prevLabel = options.prevLabel;
    if (options.nextLabel) nav.dataset.nextLabel = options.nextLabel;
    host.appendChild(nav);
  } else {
    nav.dataset.track = track.id;
    if (typeof nav.bind === 'function') nav.bind(track);
  }
  return nav;
}

window.BL = window.BL || {};
window.BL.railNav = railNav;
window.BL.measureViewport = measureScrollbar;
